/**
 * Court of Lions — a Moorish courtyard used as a route.
 *
 * The visitor enters an arcaded walkway that runs the whole perimeter; inside
 * it the ground drops its ceiling and opens to a glowing sky, with a low
 * octagonal fountain at the crossing of the two axes. The arcade is a rhythm
 * of slender columns carrying a corbelled band whose stepped profile reads as
 * a run of horseshoe arches without a single curve in the geometry.
 *
 * role: 'route' — a place you pass through and pause in, hung as a single row
 * on the walkway's back wall so the arcade frames it as you go by.
 */

import { rectSegments } from '../hang';

// The contract promises stone/water in the shared kit; fall back to the
// nearest existing finish so the room still builds against an older kit.
const mat = (kit, name, fallback) => kit.materials[name] || kit.materials[fallback];

const COL_R = 0.17; // column shaft radius
const COL_CAP = 0.26; // capital half-width
const BEAM_Y = 3.7; // springing line — clear of the 3.6m door heads
const BAY = 1.9; // target column spacing
const WALK = 2.15; // clear walkway between wall face and column centreline

const courtOfLions = {
  id: 'court',
  role: 'route',
  weight: 2,

  plan(rand, { axis }) {
    const long = 13 + rand() * 3; // 13–16
    const cross = 12 + rand() * 2; // 12–14
    return {
      w: axis === 'x' ? long : cross,
      d: axis === 'x' ? cross : long,
      height: 7,
      params: { fountainR: 1.75 + rand() * 0.5 },
    };
  },

  segments(node) {
    return rectSegments(node, { longStyle: 'row', shortStyle: 'row', margin: 1.2 });
  },

  build(node, T) {
    const { WALL_T, DOOR_W } = T.PALACE;
    const THREE = T.THREE;
    const height = node.height;
    const cx = (node.x1 + node.x2) / 2;
    const cz = (node.z1 + node.z2) / 2;

    // Interior face rectangle, then the column centreline rectangle inside it.
    const xI1 = node.x1 + WALL_T;
    const xI2 = node.x2 - WALL_T;
    const zI1 = node.z1 + WALL_T;
    const zI2 = node.z2 - WALL_T;
    const kx1 = xI1 + WALK;
    const kx2 = xI2 - WALK;
    const kz1 = zI1 + WALK;
    const kz2 = zI2 - WALK;

    // The open court is everything inside the colonnade; the sky sits over it.
    const courtW = kx2 - kx1 + 0.6;
    const courtD = kz2 - kz1 + 0.6;

    const { group, colliders } = T.standardShell(node, T.kit, {
      wallMaterial: T.kit.wallFor(node.palette || 0, true),
      floorMaterial: T.kit.materials.marble,
      laylight: { w: courtW, d: courtD },
      height,
    });

    // --- paving: the court is laid in a different stone from the walkway.
    // Sunk into the floor slab so no face is coplanar with it.
    const paving = [T.box(kx1 - 0.3, kx2 + 0.3, -0.06, 0.03, kz1 - 0.3, kz2 + 0.3)];
    group.add(T.mergedMesh(paving, mat(T.kit, 'terracotta', 'floor')));

    // --- column positions -------------------------------------------------
    // A door's bay is left open. The test is the door's whole approach
    // corridor in 2D, not just its own side, because a column belonging to
    // the perpendicular run can still land in front of a door near a corner.
    const inDoorway = (px, pz) =>
      (node.doors || []).some((d0) => {
        const halfW = (d0.w || DOOR_W) / 2 + 0.75;
        const ex = d0.side === 'E' ? node.x2 : d0.side === 'W' ? node.x1 : d0.at;
        const ez = d0.side === 'N' ? node.z2 : d0.side === 'S' ? node.z1 : d0.at;
        const ix = d0.side === 'E' ? -1 : d0.side === 'W' ? 1 : 0;
        const iz = d0.side === 'N' ? -1 : d0.side === 'S' ? 1 : 0;
        const depth = (px - ex) * ix + (pz - ez) * iz; // into the room
        const lateral = Math.abs((px - ex) * iz - (pz - ez) * ix);
        return depth > -0.6 && depth < 4.4 && lateral < halfW;
      });

    const rowPositions = (a1, a2) => {
      const n = Math.max(2, Math.round((a2 - a1) / BAY));
      const out = [];
      for (let i = 0; i <= n; i++) out.push(a1 + ((a2 - a1) * i) / n);
      return out;
    };

    // One run per side of the court. `ends: false` drops the endpoints so the
    // corner columns are owned by the N/S runs only, never doubled up.
    const runs = [
      { horizontal: true, fixed: kz2, all: rowPositions(kx1, kx2), ends: true },
      { horizontal: true, fixed: kz1, all: rowPositions(kx1, kx2), ends: true },
      { horizontal: false, fixed: kx2, all: rowPositions(kz1, kz2), ends: false },
      { horizontal: false, fixed: kx1, all: rowPositions(kz1, kz2), ends: false },
    ];

    const shaftGeos = [];
    const bandGeos = [];

    for (const run of runs) {
      // Corner columns belong to the N/S runs; E/W runs drop their endpoints
      // so no two shafts occupy the same spot.
      const kept = run.all.filter((a, i) => {
        if (!run.ends && (i === 0 || i === run.all.length - 1)) return false;
        return !inDoorway(
          run.horizontal ? a : run.fixed,
          run.horizontal ? run.fixed : a
        );
      });

      for (const a of kept) {
        const px = run.horizontal ? a : run.fixed;
        const pz = run.horizontal ? run.fixed : a;
        const shaft = new THREE.CylinderGeometry(COL_R * 0.88, COL_R, BEAM_Y - 0.62, 10, 1);
        shaft.translate(px, 0.28 + (BEAM_Y - 0.62) / 2, pz);
        shaftGeos.push(shaft);
        // Square base and capital block, so the shaft lands like masonry.
        bandGeos.push(
          T.box(px - COL_CAP, px + COL_CAP, 0, 0.3, pz - COL_CAP, pz + COL_CAP),
          T.box(px - COL_CAP, px + COL_CAP, BEAM_Y - 0.36, BEAM_Y + 0.05, pz - COL_CAP, pz + COL_CAP)
        );
        colliders.push({ circle: [px, pz, COL_CAP + 0.05], top: BEAM_Y });
      }

      // --- corbelled band: the stepped stand-in for a horseshoe arcade.
      // Steps march inward-and-upward from each springing point, then a full
      // spandrel and a proud cornice strip close the run.
      const t1 = run.fixed - 0.19;
      const t2 = run.fixed + 0.19;
      const strip = (a1, a2, y0, y1, out = 0) => {
        if (a2 - a1 < 0.02) return;
        bandGeos.push(
          run.horizontal
            ? T.box(a1, a2, y0, y1, t1 - out, t2 + out)
            : T.box(t1 - out, t2 + out, y0, y1, a1, a2)
        );
      };

      // The band spans the whole run whether or not columns were skipped.
      const spanFrom = run.all[0];
      const spanTo = run.all[run.all.length - 1];
      const bayEdges = kept.length >= 2 ? kept : [spanFrom, spanTo];
      for (let i = 0; i + 1 < bayEdges.length; i++) {
        const a0 = bayEdges[i];
        const a1 = bayEdges[i + 1];
        const s = Math.min(0.34, (a1 - a0) / 8);
        for (let k = 1; k <= 3; k++) {
          const y0 = BEAM_Y + (k - 1) * 0.15;
          const y1 = y0 + 0.15;
          strip(a0, a0 + k * s, y0, y1);
          strip(a1 - k * s, a1, y0, y1);
        }
      }
      strip(spanFrom - COL_CAP, spanTo + COL_CAP, BEAM_Y + 0.45, BEAM_Y + 0.72);
      strip(spanFrom - COL_CAP, spanTo + COL_CAP, BEAM_Y + 0.72, BEAM_Y + 0.86, 0.09);
    }

    const stone = mat(T.kit, 'stone', 'marble');
    if (shaftGeos.length) group.add(T.mergedMesh(shaftGeos, stone));
    if (bandGeos.length) group.add(T.mergedMesh(bandGeos, stone));

    // --- fountain: an octagonal curb with a still water dish, ankle high.
    // Broad enough to be the middle of the court; you step over the curb and
    // walk straight across it.
    const fr = node.params?.fountainR ?? 1.9;
    const curb = new THREE.CylinderGeometry(fr, fr + 0.1, 0.42, 8, 1);
    curb.translate(cx, 0.21, cz);
    const step = new THREE.CylinderGeometry(fr + 0.55, fr + 0.62, 0.14, 8, 1);
    step.translate(cx, 0.07, cz);
    group.add(T.mergedMesh([curb, step], mat(T.kit, 'stone', 'marble')));

    const dish = new THREE.Mesh(
      new THREE.CircleGeometry(fr - 0.12, 8),
      mat(T.kit, 'water', 'laylight')
    );
    dish.rotation.x = -Math.PI / 2;
    dish.position.set(cx, 0.34, cz);
    dish.matrixAutoUpdate = false;
    dish.updateMatrix();
    group.add(dish);
    colliders.push({ circle: [cx, cz, fr + 0.68], top: 0.42, soft: true });

    // Daylight bouncing off the court floor.
    const light = new THREE.PointLight(0xfff2dc, 22, Math.max(courtW, courtD) * 1.6, 1.9);
    light.position.set(cx, 4.6, cz);
    group.add(light);

    return { group, colliders };
  },
};

export default courtOfLions;
